import React from "react";
import { FOUNDER, CERTIFICATIONS } from "../lib/siteData";
import { Quote, Award, BadgeCheck } from "lucide-react";
import Reveal from "./Reveal";

export default function Founder() {
  return (
    <section
      id="founder"
      className="relative bg-[#F8FAFC] py-20 lg:py-28 overflow-hidden"
      data-testid="founder-section"
    >
      <div className="absolute inset-0 blueprint-grid opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#123E73]" />
            <span className="text-xs tracking-[0.3em] font-semibold text-[#123E73]">LEADERSHIP</span>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-end mb-14 lg:mb-20">
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#0A192F]">
              Meet the <span className="text-[#123E73]">Founder.</span>
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              Two decades of Caribbean and UK engineering leadership — translated into a firm
              that owns every phase of your project end-to-end.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Portrait */}
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                <img
                  src={FOUNDER.photo}
                  alt={FOUNDER.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              {/* Name card overlay */}
              <div className="absolute left-0 -bottom-6 lg:-bottom-8 bg-[#0A192F] text-white px-7 py-5 lg:px-9 lg:py-6 max-w-[85%] shadow-xl" data-testid="founder-name-card">
                <div className="text-[11px] tracking-[0.3em] font-semibold text-[#F5A400] mb-1">FOUNDER</div>
                <div className="font-display text-xl lg:text-2xl font-bold leading-tight tracking-tight">
                  {FOUNDER.name}
                </div>
                <div className="text-sm text-white/70 mt-1">{FOUNDER.role}</div>
              </div>
              {/* Accent corner */}
              <div className="hidden lg:block absolute -right-6 -top-6 w-32 h-32 border-2 border-[#F5A400] pointer-events-none" />
            </div>
          </Reveal>

          {/* Bio + stats */}
          <Reveal className="lg:col-span-7" delay={120}>
            <Quote className="w-10 h-10 text-[#F5A400]/40 mb-4" />
            <div className="space-y-5">
              {FOUNDER.bio.map((p, i) => (
                <p key={i} className="text-base lg:text-[17px] text-slate-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8" data-testid="founder-highlights">
              {FOUNDER.highlights.map((h) => (
                <div key={h.label}>
                  <div className="font-display text-2xl lg:text-3xl font-bold text-[#123E73] tracking-tight">
                    {h.value}
                  </div>
                  <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-slate-500 mt-1.5">
                    {h.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Certifications */}
        <div className="mt-20 lg:mt-28">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#123E73]" />
              <span className="text-xs tracking-[0.3em] font-semibold text-[#123E73]">CERTIFICATIONS</span>
            </div>
            <h3 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-[#0A192F] max-w-3xl">
              Licensed. Credentialed. Accountable.
            </h3>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5" data-testid="founder-certifications">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.body} delay={i * 60}>
                <div
                  className="group flex items-start gap-4 p-5 lg:p-6 bg-white border border-slate-200 hover:border-[#123E73] hover:shadow-[0_14px_40px_-20px_rgba(18,62,115,0.35)] transition-all duration-500"
                  data-testid={`cert-${c.body.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-[#F5A400]/15 text-[#123E73] shrink-0 transition-all duration-500 group-hover:bg-[#F5A400] group-hover:text-[#0A192F]">
                    <BadgeCheck className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#123E73] mb-1">
                      {c.title}
                    </div>
                    <div className="font-display text-base lg:text-lg font-bold text-[#0A192F] leading-tight">
                      {c.body}
                    </div>
                    {c.id && (
                      <div className="mt-2 text-xs text-slate-500 font-mono">
                        Registration No.&nbsp;
                        <span className="text-[#0A192F] font-semibold">{c.id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
